import { useState, useEffect, useRef, useMemo } from 'react';
import Box from '@mui/material/Box';
import { RefreshButton } from 'components';
import { getTablesTheme } from 'components/CustomThemes';
import { lighten, ThemeProvider } from '@mui/material/styles';
import {
  MaterialReactTable,
  MRT_ShowHideColumnsButton,
  MRT_ToggleGlobalFilterButton,
  MRT_GlobalFilterTextField,
  useMaterialReactTable,
} from 'material-react-table';
import PropTypes from 'prop-types';
import intl from 'react-intl-universal';
import { TABLE_LOCALE, TABLE_STATE_KEYS } from 'shared/constants/tableOptions';
import { USER_DATA } from 'shared/constants/users';
import Stack from '@mui/material/Stack';
import { Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import CustomRangePicker from 'components/CustomRangePicker';
import CustomToolbarSelectBatchEdit from 'components/HistoryTable/components/BatchEdit';
import logger from 'utils/logger';

const log = logger.createLogger('HistoryTable');

const propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  darkMode: PropTypes.bool.isRequired,
  handleRefreshClick: PropTypes.func.isRequired,
  tableTitle: PropTypes.string.isRequired,
  timeRangeStart: PropTypes.instanceOf(Date).isRequired,
  timeRangeEnd: PropTypes.instanceOf(Date).isRequired,
  updateTimeRangeStart: PropTypes.func.isRequired,
  updateTimeRangeEnd: PropTypes.func.isRequired,
  setBatchEditModalIsOpen: PropTypes.func,
  setSelectedIssueIndexes: PropTypes.func,
  enableRowSelection: PropTypes.bool.isRequired,
  onRowModelChange: PropTypes.func,
};

const HistoryTable = ({
  columns,
  data,
  darkMode,
  handleRefreshClick,
  tableTitle,
  timeRangeStart,
  timeRangeEnd,
  updateTimeRangeStart,
  updateTimeRangeEnd,
  setBatchEditModalIsOpen,
  setSelectedIssueIndexes,
  enableRowSelection,
  onRowModelChange,
}) => {
  // Adding null checking
  onRowModelChange = onRowModelChange || (() => {});

  const isInitialMount = useRef(true);
  const { pathname } = useLocation();
  const pathnameRef = useRef(pathname);

  // Closing the picker when the location changes
  useEffect(() => {
    if (pathname !== pathnameRef.current) {
      setIsStartDatePickerOpen(false);
      setIsEndDatePickerOpen(false);
    }
  }, [pathname]);

  const locale = localStorage.getItem(USER_DATA.PREFFERED_LANGUAGE);
  const [isStartDatePickerOpen, setIsStartDatePickerOpen] = useState(false);
  const [isEndDatePickerOpen, setIsEndDatePickerOpen] = useState(false);
  const [rowSelection, setRowSelection] = useState({});

  const [pagination, setPagination] = useState(() => {
    const saved = sessionStorage.getItem(TABLE_STATE_KEYS.HISTORY_PAGINATION);
    return saved ? JSON.parse(saved) : { pageIndex: 0, pageSize: 10 };
  });
  const [sorting, setSorting] = useState(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return [];
    }
    const saved = sessionStorage.getItem(TABLE_STATE_KEYS.HISTORY_SORTING);
    return saved ? JSON.parse(saved) : [];
  });
  const [columnFilters, setColumnFilters] = useState([]);

  useEffect(() => {
    try {
      validateColumns(columns);
    } catch (error) {
      log.error('HistoryTable: Column validation failed', {
        error: error.message,
        columns: columns?.length,
      });
      // You might want to show an error message to the user here
    }
  }, [columns]);

  useEffect(() => {
    sessionStorage.setItem(
      TABLE_STATE_KEYS.HISTORY_PAGINATION,
      JSON.stringify(pagination)
    );
  }, [pagination]);

  useEffect(() => {
    sessionStorage.setItem(
      TABLE_STATE_KEYS.HISTORY_SORTING,
      JSON.stringify(sorting)
    );
  }, [sorting]);

  const table = useMaterialReactTable({
    columns,
    data,
    localization: TABLE_LOCALE[locale] || TABLE_LOCALE.ja,
    muiTableContainerProps: {
      sx: { maxHeight: '950px' },
    },
    // TODO Dark Mode
    muiTableHeadProps: {
      sx: {
        backgroundColor: '#ffffff',
      },
    },
    muiTablePaperProps: {
      sx: {
        backgroundColor: '#ffffff',
      },
    },
    muiBottomToolbarProps: {
      sx: {
        backgroundColor: '#ffffff',
      },
    },
    muiTableBodyCellProps: {
      sx: {
        backgroundColor: '#ffffff', // Set cells background to white
      },
    },
    muiTableHeadRowProps: {
      sx: {
        backgroundColor: '#ffffff', // Light gray for header contrast
      },
    },
    enableColumnFilterModes: false,
    enableGrouping: true,
    enableColumnActions: false,
    enableColumnDragging: false,
    columnFilterDisplayMode: 'popover',
    enableFacetedValues: true,
    enableRowSelection: enableRowSelection,
    paginationDisplayMode: 'pages',
    positionToolbarAlertBanner: 'bottom',
    autoResetPageIndex: false,
    initialState: {
      showColumnFilters: false,
      showGlobalFilter: false,
      density: 'compact',
    },
    muiPaginationProps: {
      rowsPerPageOptions: [5, 10, 20, 30],
    },
    onPaginationChange: (updatedPagination) => {
      setPagination(updatedPagination);
      setIsStartDatePickerOpen(false);
      setIsEndDatePickerOpen(false);
      setTimeout(() => {
        onRowModelChange && onRowModelChange(table.getRowModel().rows);
      }, 500);
    },
    onSortingChange: (updatedSorting) => {
      setSorting(updatedSorting);
      setIsStartDatePickerOpen(false);
      setIsEndDatePickerOpen(false);
      setTimeout(() => {
        onRowModelChange && onRowModelChange(table.getRowModel().rows);
      }, 500);
    },
    onColumnFiltersChange: (updatedColumnFilters) => {
      setColumnFilters(updatedColumnFilters);
      setIsStartDatePickerOpen(false);
      setIsEndDatePickerOpen(false);
      setTimeout(() => {
        onRowModelChange && onRowModelChange(table.getRowModel().rows);
      }, 500);
    },
    getFacetedUniqueValues: (table, columnId) => () => {
      try {
        const uniqueValueMap = new Map();

        // Validate table and columnId
        if (!table || !columnId) {
          log.warn('getFacetedUniqueValues: Invalid table or columnId', {
            table: !!table,
            columnId,
          });
          return uniqueValueMap;
        }

        const rows = table.getPreFilteredRowModel()?.rows;
        if (!Array.isArray(rows)) {
          log.warn('getFacetedUniqueValues: Invalid rows data', {
            rowsType: typeof rows,
          });
          return uniqueValueMap;
        }

        rows.forEach((row, index) => {
          try {
            // Safely access row data
            if (!row?.original) {
              log.warn('getFacetedUniqueValues: Row missing original data', {
                rowIndex: index,
              });
              return;
            }

            const value = row.original[columnId];
            if (Array.isArray(value)) {
              value.forEach((tag) => {
                uniqueValueMap.set(tag, (uniqueValueMap.get(tag) || 0) + 1);
              });
            } else if (value !== undefined && value !== null) {
              // Handle non-array values
              uniqueValueMap.set(value, (uniqueValueMap.get(value) || 0) + 1);
            }
          } catch (error) {
            log.error('getFacetedUniqueValues: Error processing row', {
              error,
              row,
            });
          }
        });
        return uniqueValueMap;
      } catch (error) {
        log.error('getFacetedUniqueValues', error);
        return new Map();
      }
    },
    renderTopToolbar: ({ table }) => {
      const selectedRows = table.getSelectedRowModel().rows;
      return (
        <Stack sx={{ m: '2rem 0' }}>
          <Box sx={{ pl: '10px' }}>
            <Typography variant="h4" style={{ fontWeight: 'bold' }}>
              {tableTitle}
            </Typography>
          </Box>
          <Box
            sx={(theme) => ({
              backgroundColor: lighten(theme.palette.background.default, 0.05),
              display: 'flex',
              gap: '0.5rem',
              p: '8px',
              justifyContent: 'space-between',
            })}>
            <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <CustomRangePicker
                isStartDatePickerOpen={isStartDatePickerOpen}
                setIsStartDatePickerOpen={setIsStartDatePickerOpen}
                isEndDatePickerOpen={isEndDatePickerOpen}
                setIsEndDatePickerOpen={setIsEndDatePickerOpen}
                timeRangeStart={timeRangeStart}
                timeRangeEnd={timeRangeEnd}
                updateTimeRangeStart={updateTimeRangeStart}
                updateTimeRangeEnd={updateTimeRangeEnd}
                darkMode={darkMode}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              {}
              <MRT_GlobalFilterTextField table={table} />
              {}
              <MRT_ToggleGlobalFilterButton table={table} />
              {}
              <MRT_ShowHideColumnsButton table={table} />
              <RefreshButton
                darkMode={darkMode}
                tooltipTitle={intl.get(
                  'dashboard_refresh_button_tooltip_title'
                )}
                handleRefreshClick={() => {
                  handleRefreshClick();
                  table.resetRowSelection();
                }}
              />
              {enableRowSelection && (
                <CustomToolbarSelectBatchEdit
                  table={table}
                  selectedRows={selectedRows}
                  setBatchEditModalIsOpen={() => {
                    setBatchEditModalIsOpen(true);
                    table.resetRowSelection();
                  }}
                  setSelectedIssueIndexes={setSelectedIssueIndexes}
                />
              )}
            </Box>
          </Box>
        </Stack>
      );
    },
    onRowSelectionChange: (updater) => {
      setIsStartDatePickerOpen(false);
      setIsEndDatePickerOpen(false);
      setRowSelection(updater);
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (event) => {
        setIsStartDatePickerOpen(false);
        setIsEndDatePickerOpen(false);
        row.getToggleSelectedHandler()(event);
      },
      sx: {
        cursor: 'pointer',
      },
    }),
    state: {
      isLoading: !data,
      pagination,
      sorting,
      columnFilters,
      rowSelection,
    },
  });

  //! The following memo is to save the state of the table rows for the global filter
  const rowsJson = useMemo(
    () => JSON.stringify(table.getFilteredRowModel().rows),
    [table.getFilteredRowModel().rows]
  );

  useEffect(() => {
    // Checking if global filter is not empty
    const globalFilter = table.getState().globalFilter;
    if (globalFilter) {
      setTimeout(() => {
        const totalFilteredRows = table.getFilteredRowModel().rows;
        const startIndex = pagination.pageIndex * pagination.pageSize;
        const endIndex = Math.min(
          startIndex + pagination.pageSize,
          totalFilteredRows.length
        );
        const visibleRows = totalFilteredRows.slice(startIndex, endIndex);
        onRowModelChange(visibleRows);
      }, 700);
    } else {
      onRowModelChange(table.getRowModel().rows);
    }
  }, [rowsJson, pagination]);

  return (
    <Box>
      <ThemeProvider theme={getTablesTheme(darkMode)}>
        <MaterialReactTable table={table} />
      </ThemeProvider>
    </Box>
  );
};

HistoryTable.propTypes = propTypes;

export default HistoryTable;

// Adding validation to the columns
const validateColumns = (columns) => {
  if (!Array.isArray(columns)) {
    throw new Error('Columns must be an array');
  }

  columns.forEach((column, index) => {
    // Validate required properties
    if (!column.accessorKey && !column.accessorFn) {
      throw new Error(
        `Column at index ${index} must have either accessorKey or accessorFn`
      );
    }
    // Validate filterSelectOptions if present
    if (
      column.filterSelectOptions &&
      !Array.isArray(column.filterSelectOptions)
    ) {
      throw new Error(
        `filterSelectOptions must be an array for column at index ${index}`
      );
    }
  });

  return true;
};
